import {
	Badge,
	Box,
	Container,
	Divider,
	Heading,
	Icons,
	Inline,
	RelativeTime,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { ListView, Search, useSearch } from "@sembark-travel/ui-list"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useEffect } from "react"
import { generatePath } from "../router-utils"
import { XHR, TAutomation } from "./store"

function formatStartType(startType: TAutomation["start_type"]) {
	return startType === "schedule" ? "Scheduled" : "Event"
}

export function AutomationsList() {
	const [query, setQuery] = useLocationQuery()
	const [params, setParams] = useSearch(query)

	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	return (
		<Search
			title="Automations"
			initialParams={params}
			onSearch={(nextParams) => setParams({ ...nextParams, page: 1 })}
			resetParams={{ q: "", page: 1 }}
			actions={
				<ButtonLink to={generatePath("/automations/new")} level="primary">
					<Icons.Plus /> New Automation
				</ButtonLink>
			}
		>
			<ListView<TAutomation>
				pageKey={`automations`}
				params={params}
				onPageChange={(page) => setParams({ ...params, page })}
				fetch={(xhr, listParams) => XHR(xhr).get(listParams)}
				emptyState={({ searched, defaultValue }) =>
					searched ? (
						<>{defaultValue}</>
					) : (
						<Container paddingY="8">
							<Stack gap="4">
								<Text fontSize="md" fontWeight="semibold">
									Automations help you turn business events and scheduled
									checkpoints into repeatable action flows.
								</Text>
								<Divider sm />
								<Heading as="h3">What you can manage here</Heading>
								<Text>
									Create automation definitions, edit their communication setup,
									and remove ones you no longer need.
								</Text>
							</Stack>
						</Container>
					)
				}
			>
				{({ items }) => (
					<Table
						bordered
						hover
						responsive
						headers={["Automation", "Start", "Status", "Last Run", "Created"]}
						rows={items.map((automation) => [
							<Stack gap="1" key={automation.id}>
								<Link
									to={generatePath("/automations/:automationId", {
										automationId: String(automation.id),
									})}
									anchored
									color="accent"
								>
									<Text fontWeight="semibold">
										{automation.source?.name || automation.source_key}
									</Text>
								</Link>
								<Inline gap="2" flexWrap="wrap">
									{automation.template?.name ? (
										<Badge success>{automation.template.name}</Badge>
									) : null}
									<Text color="muted" fontSize="sm">
										{automation.steps_count ?? automation.steps?.length ?? 0}{" "}
										step
										{(automation.steps_count ??
											automation.steps?.length ??
											0) !== 1
											? "s"
											: ""}
									</Text>
								</Inline>
							</Stack>,
							<Stack gap="1">
								<Text>{formatStartType(automation.start_type)}</Text>
								<Text color="muted" fontSize="sm">
									{automation.start_type === "schedule"
										? String(
												(automation.start && "field" in automation.start
													? automation.start.field
													: "") || "custom schedule"
											)
										: String(
												(automation.start && "event" in automation.start
													? automation.start.event
													: "") || "event"
											)}
								</Text>
							</Stack>,
							<Inline gap="1" alignItems="center">
								<Box>
									<Badge
										success={automation.is_active}
										warning={!automation.is_active}
									>
										{automation.is_active ? "Active" : "Inactive"}
									</Badge>
								</Box>
								<Text color="muted" fontSize="sm">
									{automation.total_runs_count} run
									{automation.total_runs_count !== 1 ? "s" : ""}
								</Text>
							</Inline>,
							automation.last_run_at ? (
								<RelativeTime timestamp={automation.last_run_at} />
							) : (
								<Text color="muted">Never</Text>
							),
							<RelativeTime timestamp={automation.created_at} />,
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}
